import { AfterViewInit, Component, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { BLOG } from '../../data/blog/blog.data';
import { URLS } from '../../data/navigation/navigation.data';
import { isThemeFree } from '../../model/product/product.model';
import { AnalyticsService } from '../../service/analytics/analytics.service';
import { ColorService } from '../../service/color/color.service';
import { StoreRatingSummary, StoreReviews, StoreService } from '../../service/store/store.service';
import { Review } from '../../model/review/review.model';
import { Subscription } from 'rxjs';

@Component( {
  selector: 'app-color-detail',
  templateUrl: './color-detail.component.html',
  styleUrls: [ './color-detail.component.scss' ],
} )
export class ColorDetailComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  URLS = URLS;

  // Hex strings driving every section (replaces the palette page's internal
  // `palette` array). Mapped to { value } objects internally so the ported
  // method bodies stay faithful to the source.
  @Input() colors: string[] = [];

  // When set (the theme / theme/content pages), a "Color Palette" section
  // renders above Color Channels - the 10 marketing colors as harmony-style
  // squares plus a CTA into the palette page (/color-palette?product=...).
  @Input() paletteLink?: { product: string, category: string };

  // Named palette colors for the Color Palette section tooltips (optional).
  @Input() colorNames: string[] = [];

  // The full named palette (24 colors). When set, the Color Palette section
  // renders every color as a named tile with its hex (the palette pages)
  // instead of the 10-swatch marketing row (the theme pages).
  @Input() namedPalette: { name: string, hex: string }[] = [];

  // In the Base24 scheme the last 8 colors are the semantic status accents
  // (info, success, warning, error, each with a focused variant). We split them
  // out into their own labelled group so they read as UI indicators, not part
  // of the core palette. Only a full 24-colour palette is split.
  private static readonly STATUS_COLOR_COUNT = 8;

  get coreNamedColors(): { name: string, hex: string }[] {
    return this.namedPalette.length === 24
      ? this.namedPalette.slice( 0, this.namedPalette.length - ColorDetailComponent.STATUS_COLOR_COUNT )
      : this.namedPalette;
  }

  get statusNamedColors(): { name: string, hex: string }[] {
    return this.namedPalette.length === 24
      ? this.namedPalette.slice( this.namedPalette.length - ColorDetailComponent.STATUS_COLOR_COUNT )
      : [];
  }

  // The theme behind this palette (the palette pages): rendered as a single
  // poster card in a Related Theme section, like the color page's Matching
  // Themes.
  @Input() relatedTheme?: { product: any, category: string };

  // Label for the sidebar's overview button (scrolls to top). The palette pages
  // call this a "Palette"; the theme pages pass "Theme".
  @Input() overviewLabel = 'Palette';

  // Platform previews (theme pages only): one labelled screenshot per platform,
  // in coverage order. When present, a "Platforms" section renders right after
  // the Color Palette section, one screenshot per row, each opening a lightbox.
  @Input() platformPreviews: { name: string, logo: string, src: string }[] = [];

  // Theme name used only for the platform screenshots' alt text.
  @Input() platformThemeName = '';

  // Lightbox caption pill (theme pages): the pill is the theme's CARD/panel
  // background with the theme's default text; the platform logo rides in a
  // circle filled with the theme's EDITOR background, and both pill + circle are
  // ringed by a light tone of the theme colour. All empty on non-theme pages
  // (falls back to neutral site surfaces).
  @Input() pillCardBg = '';
  @Input() pillFg = '';
  @Input() pillEditorBg = '';
  @Input() pillAccent = '';
  // Border tone target: for a DARK theme the accent is mixed toward black (a
  // darker ring), for a LIGHT theme toward white — keyed to the THEME's own
  // appearance, not the site's light/dark mode.
  @Input() pillTone = '';

  // The platform whose screenshot is enlarged in the lightbox, or null (closed).
  lightboxPreview: { name: string, logo: string, src: string } | null = null;

  palette: { value: string }[] = [];

  // "On This Page" sidebar sections (the palette swatch is the implicit "top").
  pageSections: { key: string; label: string }[] = [];

  private static readonly BASE_SECTIONS: { key: string; label: string }[] = [
    { key: 'channels', label: 'Color Channels' },
    { key: 'harmony', label: 'Harmony' },
    { key: 'colorVision', label: 'Color Vision' },
    { key: 'blog', label: 'From The Blog' },
  ];
  activeSection = 'top';
  private sectionObserver?: IntersectionObserver;

  // Aggregate channel stats for the whole palette (RGB weight + average HSL /
  // HSV / CMYK).
  channelStats: { rgb: any[]; hsl: any[]; hsv: any[]; cmyk: any[] } | null = null;

  // Three blog posts: the shown theme's own posts first (see blogThemeKey), random
  // posts, reshuffled per load, for the rest.
  blogCards = [ ...BLOG ].sort( () => Math.random() - 0.5 ).slice( 0, 3 );

  /** The theme this detail belongs to. Its own posts, the color by color anatomy
   *  teardown first, lead the "From The Blog" cards, so a theme page links its
   *  teardown every time instead of three random posts. */
  @Input() set blogThemeKey( key: string | undefined ) {
    const own = key
      ? BLOG.filter( p => ( p.relatedThemes || [] ).includes( key ) )
          .sort( ( a, b ) => Number( b.key.startsWith( 'anatomy-of-the-' ) ) - Number( a.key.startsWith( 'anatomy-of-the-' ) ) )
      : [];
    const rest = BLOG.filter( p => !own.includes( p ) ).sort( () => Math.random() - 0.5 );
    this.blogCards = [ ...own, ...rest ].slice( 0, 3 );
  }

  harmonyResult = [];

  analogous = [];
  complementaries = [];
  triadics = [];
  temperatures = [];

  // Per-scheme harmonic scores, listed in the SAME order as the color tool's
  // Harmony section (Analogous, Monochrome, Complementary, Split Complementary,
  // Triadic, Tetradic).
  harmonyScores: { name: string; description: string; score: number; counts: number[] }[] = [];

  protanomalyColors = [];
  deuteranomalyColors = [];
  tritanomalyColors = [];
  protanopiaColors = [];
  deuteranopiaColors = [];
  tritanopiaColors = [];
  achromatomalyColors = [];
  achromatopsiaColors = [];

  /**
   * The All In One bundle (owner, 2026-10-04): no palette of its own, so the page is its included
   * themes (projected as [cpIncludes]), the platforms, the blog and the reviews collected from the
   * marketplaces. It is not sold on the site, so there is no Write a Review.
   */
  @Input() bundleMode = false;
  /** the tool pages' design (the palette page, owner 2026-10-15): a chips' bar for the sidebar, every section as two cards */
  @Input() boxes = false;
  @Input() includesLabel = 'Included Themes';
  /** the sidebar swatch of a page without a palette (the bundle: its themes' accents) */
  @Input() swatchColors: string[] = [];
  /** buyers rate from their order page; false where the site does not sell the item */
  @Input() reviewWritable = true;

  /** Theme pages: whose buyer ratings the Reviews section shows (owner, 2026-10-04) */
  @Input() reviewProductKey = '';
  /** the selected listing's sku and platform name, for its own line in the Reviews section */
  @Input() reviewSku = '';
  @Input() reviewPlatformName = '';

  /** Theme pages: the theme's review bubbles (marketplaces + the site's buyers), the page's last section */
  @Input() reviewCards: Review[] = [];
  /** the theme's ratings on the other platforms (marketplaces) and the one rating over all of them, as on the banner */
  @Input() reviewOtherCount = 0;
  @Input() reviewAverage = 0;

  reviewSummary: StoreRatingSummary | null = null;
  reviewPlatformSummary: StoreRatingSummary | null = null;
  private storeReviews: StoreReviews | null = null;
  private reviewsSub?: Subscription;

  constructor( public router: Router, private colorService: ColorService, private zone: NgZone,
               private analyticsService: AnalyticsService, private storeService: StoreService ) {
  }

  /** Which page hosts this component (theme / palette), the `source` breakdown for the events below. */
  private get hostPage(): string {
    return ( this.router.url.split( '?' )[ 0 ] || '' ).replace( /^\//, '' ) || 'unknown';
  }

  ngOnInit(): void {
    this.recompute();
  }

  ngOnChanges( changes: SimpleChanges ): void {
    if ( changes[ 'reviewProductKey' ] && this.reviewProductKey && !this.reviewsSub ) {
      this.reviewsSub = this.storeService.reviews().subscribe( data => {
        this.storeReviews = data;
        this.applyReviews();
        this.buildSections();
        // the Reviews section may have just appeared: let the scroll-spy see it
        this.initSectionObserver();
      } );
    }
    this.applyReviews();
    this.buildSections();
    if ( changes[ 'reviewCards' ] && !changes[ 'reviewCards' ].firstChange ) {
      // the Reviews section may have just appeared: let the scroll-spy see it
      this.initSectionObserver();
    }
    if ( changes[ 'colors' ] ) {
      this.recompute();
    }
  }

  /** a theme page: its last section is always Reviews (owner, 2026-10-04), with a word for the first reviewer when empty */
  get hasReviews(): boolean {
    return !!this.reviewProductKey && ( this.reviewWritable || this.anyReviews );
  }

  /** the theme has a rating or a comment to show */
  get anyReviews(): boolean {
    return this.reviewBuyerCount + this.reviewOtherCount > 0 || this.reviewCards.length > 0;
  }

  get reviewBuyerCount(): number {
    return this.reviewSummary?.count || 0;
  }

  /** "Get <Theme>": back to the top of the page, where the platforms and the CTA are */
  scrollToTop(): void {
    try {
      window.scrollTo( { top: 0, behavior: 'smooth' } );
      document.body.scrollTo( { top: 0, behavior: 'smooth' } );
    } catch {
      window.scrollTo( 0, 0 );
    }
  }

  /** the theme's ratings and the selected listing's */
  private applyReviews(): void {
    const data = this.storeReviews;
    const key = this.reviewProductKey;
    this.reviewSummary = data && key ? data.products[ key ] ?? null : null;
    this.reviewPlatformSummary = data && this.reviewSku ? data.skus[ this.reviewSku ] ?? null : null;
  }

  private buildSections(): void {
    if ( this.bundleMode ) {
      this.pageSections = [
        { key: 'includes', label: this.includesLabel },
        ...( this.platformPreviews.length ? [ { key: 'platforms', label: 'Platforms' } ] : [] ),
        { key: 'blog', label: 'From The Blog' },
        ...( this.hasReviews ? [ { key: 'reviews', label: 'Reviews' } ] : [] ),
      ];
      return;
    }
    this.pageSections = [
      ...( this.platformPreviews.length ? [ { key: 'platforms', label: 'Platforms' } ] : [] ),
      ...( this.paletteLink || this.namedPalette.length ? [ { key: 'palette', label: 'Color Palette' } ] : [] ),
      ...ColorDetailComponent.BASE_SECTIONS.slice( 0, 3 ),
      ...( this.relatedTheme ? [ { key: 'themes', label: 'Related Themes' } ] : [] ),
      ...ColorDetailComponent.BASE_SECTIONS.slice( 3 ),
      ...( this.hasReviews ? [ { key: 'reviews', label: 'Reviews' } ] : [] ),
    ];
  }

  trackByNamedHex( _index: number, color: { hex: string } ): string {
    return color.hex;
  }

  /** CTA of the Color Palette section: open the palette detail page in a
   *  new tab (the reader keeps their place on the theme page). */
  /** /tools/wallpaper/<the palette's hexes>?theme=<key>: the wallpaper generator in this theme's colors */
  get wallpaperHref(): string {
    const hexes = this.palette.map( c => String( c.value || '' ).replace( '#', '' ).toLowerCase() ).filter( Boolean ).slice( 0, 10 );
    if ( hexes.length < 2 ) return URLS.tools.wallpaper;
    const theme = this.paletteLink ? `?theme=${ encodeURIComponent( this.paletteLink.product ) }` : '';
    return `${ URLS.tools.wallpaper }/${ hexes.join( '-' ) }${ theme }`;
  }

  onWallpaperClick(): void {
    this.analyticsService.logEvent( 'Wallpaper Initialized', { source: 'theme-page', theme: this.paletteLink?.product || null } );
  }

  goToPalette(): void {
    if ( !this.paletteLink ) {
      return;
    }
    this.analyticsService.logEvent( 'Palette Clicked', {
      key: this.paletteLink.product,
      source: this.hostPage,
    } );
    const url = this.router.serializeUrl( this.router.createUrlTree( [ URLS.colorPalette ], {
      queryParams: {
        product: this.paletteLink.product,
        category: ( this.paletteLink.category || '' ).toLowerCase(),
      },
    } ) );
    window.open( url, '_blank' );
  }

  /** Open the platform screenshot enlarged in a full-screen lightbox. */
  openLightbox( preview: { name: string, logo: string, src: string } ): void {
    this.lightboxPreview = preview;
    this.analyticsService.logEvent( 'Platform Screenshot Opened', {
      platform: preview.name,
      theme: this.platformThemeName,
      source: this.hostPage,
    } );
  }

  /** Close the platform screenshot lightbox. */
  closeLightbox(): void {
    this.lightboxPreview = null;
  }

  /** Step to the previous / next platform screenshot (wraps around), keeping the
   *  lightbox open. `dir` is -1 (prev) or +1 (next). */
  stepLightbox( dir: number, ev?: Event ): void {
    ev?.stopPropagation();
    const list = this.platformPreviews;
    if ( !this.lightboxPreview || list.length < 2 ) {
      return;
    }
    const i = list.indexOf( this.lightboxPreview );
    const next = list[ ( i + dir + list.length ) % list.length ];
    if ( next ) {
      this.openLightbox( next );
    }
  }

  @HostListener( 'document:keydown.arrowleft' )
  onArrowLeft(): void {
    if ( this.lightboxPreview ) { this.stepLightbox( -1 ); }
  }

  @HostListener( 'document:keydown.arrowright' )
  onArrowRight(): void {
    if ( this.lightboxPreview ) { this.stepLightbox( 1 ); }
  }

  @HostListener( 'document:keydown.escape' )
  onEscape(): void {
    if ( this.lightboxPreview ) {
      this.closeLightbox();
    }
  }

  /** The Related Themes card navigates on its own; this only records the
   *  click (bubbles up from the card) with the hosting page as the source. */
  onRelatedThemeClick(): void {
    if ( !this.relatedTheme ) {
      return;
    }
    this.analyticsService.logEvent( 'Related Theme Clicked', {
      theme: this.relatedTheme.product?.name,
      key: this.relatedTheme.product?.key,
      source: this.hostPage,
      // whether the theme behind this card is a paid one (Mixpanel only)
      paid: this.relatedTheme.product ? !isThemeFree( this.relatedTheme.product ) : false,
    } );
  }

  // Rebuild the internal palette from the colors input, then recompute every
  // section. Mirrors the palette page's updateColors() over the ported sections.
  private recompute(): void {
    this.palette = ( this.colors || [] )
      .filter( ( c ): c is string => typeof c === 'string' && c.length > 0 )
      .map( c => ( { value: c.startsWith( '#' ) ? c : '#' + c } ) );

    this.protanomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'protanomaly' );
    this.deuteranomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'deuteranomaly' );
    this.tritanomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'tritanomaly' );
    this.protanopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'protanopia' );
    this.deuteranopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'deuteranopia' );
    this.tritanopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'tritanopia' );
    this.achromatomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'achromatomaly' );
    this.achromatopsiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'achromatopsia' );
    this.harmonyResult = this.analyzePaletteHarmony();
    this.buildHarmonyScores();
    this.buildChannelStats();
  }

  public analyzePaletteHarmony() {
    const rgbPalette = this.palette.map( color => this.colorService.hexToRgb( color.value ) );

    const hsvPalette = rgbPalette.map( rgb => this.colorService.rgbToHsv( rgb ) );

    const hues = hsvPalette.map( hsv => hsv.h );

    let complementaryCount = 0;
    let analogousCount = 0;
    let triadicCount = 0;
    let warmCount = 0;
    let coolCount = 0;

    const huePairs = new Set();
    const hueTriplets = new Set();

    this.temperatures = [];
    this.analogous = [];
    this.complementaries = [];
    this.triadics = [];
    for ( let i = 0; i < hues.length; i++ ) {
      this.analogous.push( 0 );
      this.complementaries.push( 0 );
      this.triadics.push( 0 );
    }

    // Combine loops for hue comparison and warm/cool check
    for ( let i = 0; i < hues.length; i++ ) {
      // Check if the hue is warm or cool
      if ( this.colorService.isWarm( hues[ i ] ) ) {
        warmCount++;
        this.temperatures.push( 'warm' );
      } else if ( this.colorService.isCool( hues[ i ] ) ) {
        this.temperatures.push( 'cool' );
        coolCount++;
      }

      for ( let j = i + 1; j < hues.length; j++ ) {
        if ( !huePairs.has( `${ i },${ j }` ) && !huePairs.has( `${ j },${ i }` ) ) {
          if ( this.colorService.isComplementary( hues[ i ], hues[ j ] ) ) {
            this.complementaries[ i ] = this.complementaries[ i ] + 1;
            this.complementaries[ j ] = this.complementaries[ j ] + 1;
          }
          if ( this.colorService.isAnalogous( hues[ i ], hues[ j ] ) ) {
            this.analogous[ i ] = this.analogous[ i ] + 1;
            this.analogous[ j ] = this.analogous[ j ] + 1;
          }
          huePairs.add( `${ i },${ j }` );
        }
      }
    }

    for ( let i = 0; i < hues.length - 2; i++ ) {
      for ( let j = i + 1; j < hues.length - 1; j++ ) {
        for ( let k = j + 1; k < hues.length; k++ ) {
          const triplet = [ hues[ i ], hues[ j ], hues[ k ] ].sort().join( ',' );
          if ( !hueTriplets.has( triplet ) ) {
            if ( this.colorService.isTriadic( [ hues[ i ], hues[ j ], hues[ k ] ] ) ) {
              this.triadics[ i ] = this.triadics[ i ] + 1;
              this.triadics[ j ] = this.triadics[ j ] + 1;
              this.triadics[ k ] = this.triadics[ k ] + 1;
            }
            hueTriplets.add( triplet );
          }
        }
      }
    }

    for ( let i = 0; i < hues.length; i++ ) {
      analogousCount += this.analogous[ i ];
      complementaryCount += this.complementaries[ i ];
      triadicCount += this.triadics[ i ];
    }

    const temperatureCount = Math.max( warmCount, coolCount );

    let temperatureDecision = '';

    if ( warmCount > coolCount ) {
      temperatureDecision = 'warm';
    } else if ( warmCount < coolCount ) {
      temperatureDecision = 'cool';
    } else {
      temperatureDecision = 'equal';
    }

    return [
      {
        name: 'Analogous',
        count: analogousCount,
        max: ( this.palette.length * ( this.palette.length - 1 ) ) / 2,
      },
      {
        name: 'Complementary',
        count: complementaryCount,
        max: ( this.palette.length * ( this.palette.length - 1 ) ) / 2,
      },
      {
        name: 'Triadic',
        count: triadicCount,
        max: ( this.palette.length * ( this.palette.length - 1 ) * ( this.palette.length - 2 ) ) / 6,
      },
      {
        name: 'Temperature',
        count: temperatureCount,
        decision: temperatureDecision,
        max: this.palette.length,
      },
    ];
  }

  // Aggregate the palette into overall channel stats: each RGB channel's share of
  // the total channel sum ("weight"), and the average hue (circular mean),
  // saturation and lightness across all colours.
  private buildChannelStats(): void {
    const hexes = this.pickHeroColors();
    if ( hexes.length === 0 ) {
      this.channelStats = null;
      return;
    }
    let sumR = 0, sumG = 0, sumB = 0, sumS = 0, sumL = 0, hx = 0, hy = 0;
    let sumSv = 0, sumV = 0, sumC = 0, sumM = 0, sumY = 0, sumK = 0;
    for ( const hex of hexes ) {
      const num = parseInt( hex.replace( '#', '' ), 16 );
      const r = ( num >> 16 ) & 255;
      const g = ( num >> 8 ) & 255;
      const b = num & 255;
      sumR += r;
      sumG += g;
      sumB += b;
      const hsl = this.colorService.hexToHsl( hex.replace( '#', '' ) );
      sumS += hsl.s;
      sumL += hsl.l;
      const rad = ( hsl.h * Math.PI ) / 180;
      hx += Math.cos( rad );
      hy += Math.sin( rad );

      // HSV (value + saturation) and CMYK, straight from the 0-255 channels.
      const rf = r / 255, gf = g / 255, bf = b / 255;
      const max = Math.max( rf, gf, bf ), min = Math.min( rf, gf, bf );
      sumV += max * 100;
      sumSv += ( max === 0 ? 0 : ( max - min ) / max ) * 100;
      const k = 1 - max;
      sumK += k * 100;
      if ( k < 1 ) {
        sumC += ( ( 1 - rf - k ) / ( 1 - k ) ) * 100;
        sumM += ( ( 1 - gf - k ) / ( 1 - k ) ) * 100;
        sumY += ( ( 1 - bf - k ) / ( 1 - k ) ) * 100;
      }
    }
    const n = hexes.length;
    const total = ( sumR + sumG + sumB ) || 1;
    let avgH = ( Math.atan2( hy / n, hx / n ) * 180 ) / Math.PI;
    if ( avgH < 0 ) { avgH += 360; }
    const avgS = sumS / n;
    const avgL = sumL / n;
    const avgSv = sumSv / n;
    const avgV = sumV / n;
    const avgC = sumC / n, avgM = sumM / n, avgYy = sumY / n, avgK = sumK / n;
    const weight = ( x: number ) => Math.round( ( x / total ) * 100 );
    this.channelStats = {
      rgb: [
        { label: 'R', width: weight( sumR ), text: weight( sumR ) + '%', color: '#e0584f' },
        { label: 'G', width: weight( sumG ), text: weight( sumG ) + '%', color: '#3fb27a' },
        { label: 'B', width: weight( sumB ), text: weight( sumB ) + '%', color: '#4a86e8' },
      ],
      hsl: [
        { label: 'H', width: ( avgH / 360 ) * 100, text: Math.round( avgH ) + '°', color: `hsl(${ avgH }, 75%, 55%)` },
        { label: 'S', width: avgS, text: Math.round( avgS ) + '%', color: `hsl(${ avgH }, ${ avgS }%, 55%)` },
        { label: 'L', width: avgL, text: Math.round( avgL ) + '%', color: `hsl(0, 0%, ${ avgL }%)` },
      ],
      hsv: [
        { label: 'H', width: ( avgH / 360 ) * 100, text: Math.round( avgH ) + '°', color: `hsl(${ avgH }, 75%, 55%)` },
        { label: 'S', width: avgSv, text: Math.round( avgSv ) + '%', color: `hsl(${ avgH }, ${ avgSv }%, 55%)` },
        { label: 'V', width: avgV, text: Math.round( avgV ) + '%', color: `hsl(${ avgH }, 40%, ${ Math.max( 20, avgV ) }%)` },
      ],
      cmyk: [
        { label: 'C', width: avgC, text: Math.round( avgC ) + '%', color: '#22b3c8' },
        { label: 'M', width: avgM, text: Math.round( avgM ) + '%', color: '#cf2e8a' },
        { label: 'Y', width: avgYy, text: Math.round( avgYy ) + '%', color: '#e6c100' },
        { label: 'K', width: avgK, text: Math.round( avgK ) + '%', color: '#3a3a3a' },
      ],
    };
  }

  ngAfterViewInit(): void {
    this.initSectionObserver();
  }

  // Scroll-spy: highlight the sidebar entry for whichever section is centered.
  // Re-runnable so it can rebind after the colors input changes.
  private initSectionObserver(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    this.sectionObserver?.disconnect();
    this.sectionObserver = new IntersectionObserver( entries => {
      let changed = false;
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) {
            this.zone.run( () => ( this.activeSection = sec ) );
            changed = true;
          }
        }
      }
      if ( changed ) {
        this.scrollActivePillToStart();
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    setTimeout( () => {
      document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) );
    } );
  }

  ngOnDestroy(): void {
    this.sectionObserver?.disconnect();
    this.reviewsSub?.unsubscribe();
  }

  /** Hard-edged equal bands of the current palette for the sidebar swatch, so it
   *  reads as discrete color steps (like the palette tool icon) rather than a
   *  smooth blend. Each color gets two stops at the same boundary to keep the
   *  transition crisp. */
  paletteGradient(): string {
    const hero = this.pickHeroColors();
    const colors = hero.length ? hero : this.swatchColors;
    if ( colors.length === 0 ) {
      return 'var(--darker-contrast)';
    }
    if ( colors.length === 1 ) {
      return colors[ 0 ];
    }
    const band = 100 / colors.length;
    const stops = colors
      .map( ( c, i ) => `${ c } ${ ( i * band ).toFixed( 3 ) }%, ${ c } ${ ( ( i + 1 ) * band ).toFixed( 3 ) }%` )
      .join( ', ' );
    return `linear-gradient(90deg, ${ stops })`;
  }

  // Smooth-scroll to a section, clearing the fixed header (and, on mobile, the
  // sticky section bar). Mirrors the color tool page.
  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 )
      ? document.body
      : document.documentElement;

    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.scrollActivePillToStart();
      this.tweenScroll( scroller, 0 );
      return;
    }

    const el = document.getElementById( 'sec-' + key );
    if ( !el ) {
      return;
    }
    this.activeSection = key;
    this.scrollActivePillToStart();

    let headerOffset = 84;
    const bar = this.boxes ? document.querySelector( '.cp-chipbar' ) as HTMLElement | null : null;
    const sidebar = document.querySelector( '.cp-sidebar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    } else if ( sidebar && window.matchMedia( '(max-width: 900px)' ).matches ) {
      const stickyTop = parseFloat( getComputedStyle( sidebar ).top ) || 57;
      headerOffset = stickyTop + sidebar.offsetHeight + 12;
    }
    const target = Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset );
    this.tweenScroll( scroller, target );
  }

  private scrollActivePillToStart(): void {
    setTimeout( () => {
      const strip = document.querySelector( '.cp-sidebar' ) as HTMLElement | null;
      if ( !strip || getComputedStyle( strip ).overflowX !== 'auto' ) {
        return;
      }
      const active = strip.querySelector( '.cp-sidebar-color.active, .cp-sidebar-link.active' ) as HTMLElement | null;
      if ( !active ) {
        return;
      }
      const sr = strip.getBoundingClientRect();
      const ar = active.getBoundingClientRect();
      const target = strip.scrollLeft + ( ar.left - sr.left ) - ( sr.width - ar.width ) / 2;
      strip.scrollTo( { left: Math.max( 0, target ), behavior: 'smooth' } );
    } );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const duration = 420;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / duration );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) {
        requestAnimationFrame( step );
      }
    };
    requestAnimationFrame( step );
  }

  // Score each colour scheme the way the color tool's Harmony section lists them
  // (Analogous, Monochrome, Complementary, Split Complementary, Triadic,
  // Tetradic). A scheme's score is the share of palette colours that take part
  // in at least one matching combination on the hue wheel; counts[i] is how many
  // combinations colour i joins (drives the swatch muting + the badge).
  private buildHarmonyScores(): void {
    const hexes = this.pickHeroColors();
    const n = hexes.length;
    const hues = hexes.map( h => this.colorService.hexToHsl( h.replace( '#', '' ) ).h );

    // Shortest distance between two hues on the 360 degree wheel.
    const dist = ( a: number, b: number ): number => {
      const d = Math.abs( a - b ) % 360;
      return d > 180 ? 360 - d : d;
    };

    const pair = ( ok: ( a: number, b: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          if ( ok( hues[ i ], hues[ j ] ) ) { counts[ i ]++; counts[ j ]++; }
        }
      }
      return counts;
    };

    const triple = ( ok: ( a: number, b: number, c: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          for ( let k = j + 1; k < n; k++ ) {
            if ( ok( hues[ i ], hues[ j ], hues[ k ] ) ) { counts[ i ]++; counts[ j ]++; counts[ k ]++; }
          }
        }
      }
      return counts;
    };

    const quad = ( ok: ( a: number, b: number, c: number, d: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          for ( let k = j + 1; k < n; k++ ) {
            for ( let l = k + 1; l < n; l++ ) {
              if ( ok( hues[ i ], hues[ j ], hues[ k ], hues[ l ] ) ) {
                counts[ i ]++; counts[ j ]++; counts[ k ]++; counts[ l ]++;
              }
            }
          }
        }
      }
      return counts;
    };

    const isComp = ( a: number, b: number ) => dist( a, b ) >= 156;
    // Monochrome and analogous are kept DISJOINT so they no longer score the same:
    // monochrome is the same hue (a tight band), analogous is a NEIGHBOURING but
    // distinct hue (just outside the monochrome band, up to 40 degrees away).
    const isMono = ( a: number, b: number ) => dist( a, b ) <= 12;
    const isAnalog = ( a: number, b: number ) => dist( a, b ) > 12 && dist( a, b ) <= 40;
    const isTriad = ( a: number, b: number, c: number ) =>
      dist( a, b ) >= 90 && dist( a, b ) <= 150 &&
      dist( b, c ) >= 90 && dist( b, c ) <= 150 &&
      dist( a, c ) >= 90 && dist( a, c ) <= 150;
    // A base sitting near the complement of two colours that flank it closely.
    const isSplit = ( a: number, b: number, c: number ) => {
      const tri = [ a, b, c ];
      for ( let base = 0; base < 3; base++ ) {
        const x = tri[ ( base + 1 ) % 3 ];
        const y = tri[ ( base + 2 ) % 3 ];
        if ( dist( tri[ base ], x ) >= 126 && dist( tri[ base ], x ) <= 174 &&
             dist( tri[ base ], y ) >= 126 && dist( tri[ base ], y ) <= 174 &&
             dist( x, y ) >= 36 && dist( x, y ) <= 90 ) {
          return true;
        }
      }
      return false;
    };
    // Two complementary pairs (a rectangle/square on the wheel).
    const isTetra = ( a: number, b: number, c: number, d: number ) => {
      const pairings = [
        [ [ a, b ], [ c, d ] ],
        [ [ a, c ], [ b, d ] ],
        [ [ a, d ], [ b, c ] ],
      ];
      return pairings.some( p => isComp( p[ 0 ][ 0 ], p[ 0 ][ 1 ] ) && isComp( p[ 1 ][ 0 ], p[ 1 ][ 1 ] ) );
    };

    // RGB chroma (max - min) per colour. Near-gray tints/shades have a tiny
    // chroma and an unreliable hue (8-bit rounding swings it by many degrees at
    // high lightness), but a tint or shade still belongs to a single-hue family.
    // So for mono / analogous we let a low-chroma colour match regardless of its
    // noisy hue - this is what made a pure monochrome palette score 90% not 100%.
    const chroma = hexes.map( h => {
      const v = parseInt( h.replace( '#', '' ), 16 );
      const r = ( v >> 16 ) & 255, g = ( v >> 8 ) & 255, b = v & 255;
      return Math.max( r, g, b ) - Math.min( r, g, b );
    } );
    const lowChroma = ( i: number ): boolean => chroma[ i ] <= 28;

    const pairIdx = ( ok: ( i: number, j: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          if ( ok( i, j ) ) { counts[ i ]++; counts[ j ]++; }
        }
      }
      return counts;
    };

    const schemes: { name: string; description: string; counts: number[] }[] = [
      { name: 'Analogous', description: 'Neighbouring hues. Calm, cohesive, easy on the eyes.', counts: pairIdx( ( i, j ) => !lowChroma( i ) && !lowChroma( j ) && isAnalog( hues[ i ], hues[ j ] ) ) },
      { name: 'Monochrome', description: 'One hue, varied in lightness. Quiet, clean, simple to balance.', counts: pairIdx( ( i, j ) => ( lowChroma( i ) && lowChroma( j ) ) || isMono( hues[ i ], hues[ j ] ) ) },
      { name: 'Complementary', description: 'The opposite hue. Maximum contrast, one bold accent.', counts: pair( isComp ) },
      { name: 'Split Complementary', description: 'The two hues beside the opposite. Softer tension, more range.', counts: triple( isSplit ) },
      { name: 'Triadic', description: 'Three evenly spaced hues. Balanced but lively.', counts: triple( isTriad ) },
      { name: 'Tetradic', description: 'Two complementary pairs. The richest scheme, hardest to balance.', counts: quad( isTetra ) },
    ];

    this.harmonyScores = schemes.map( s => ( {
      name: s.name,
      description: s.description,
      // The number of palette colours that take part in this scheme (a plain count,
      // not a percentage, and not the number of combinations, so triadic's triplets
      // do not inflate it). Same measure across every tool and the theme page.
      score: s.counts.filter( c => c > 0 ).length,
      counts: s.counts,
    } ) );
  }

  // Status level for a harmony score, mirroring the readability dots: strong
  // (green) / partial (amber) / weak (red). A 0 score is weak, so it reads red.
  // Dot level from the share of the palette that takes part (count / total), so the
  // three dot sizes stay meaningful whatever the palette length.
  harmonyLevel( count: number, total: number ): string {
    const frac = total > 0 ? count / total : 0;
    if ( frac >= 0.67 ) { return 'high'; }
    if ( frac >= 0.34 ) { return 'mid'; }
    return 'low';
  }

  // Colour-vision simulations grouped by clinical category (mirrors the color
  // tool page's grouping).
  // Grouped by the affected cone (protan / deutan / tritan, plus monochromacy),
  // each family read mild (anomaly) to severe (anopia). Each row is captioned in
  // one line: bold name, then its description inline in a lighter style.
  colorVisionGroups(): { group: string; items: { name: string; note: string; colors: string[] }[] }[] {
    return [
      {
        group: 'Protan (red)',
        items: [
          { name: 'Protanomaly', note: 'Shifted L-cone (long-wavelength) sensitivity. Reds desaturate and darken.', colors: this.protanomalyColors },
          { name: 'Protanopia', note: 'No functional L-cones. Red and green hues are confused and reds appear dim.', colors: this.protanopiaColors },
        ],
      },
      {
        group: 'Deutan (green)',
        items: [
          { name: 'Deuteranomaly', note: 'Shifted M-cone (medium-wavelength) sensitivity. The most prevalent deficiency.', colors: this.deuteranomalyColors },
          { name: 'Deuteranopia', note: 'No functional M-cones. Red and green hues are confused at normal luminance.', colors: this.deuteranopiaColors },
        ],
      },
      {
        group: 'Tritan (blue)',
        items: [
          { name: 'Tritanomaly', note: 'Shifted S-cone (short-wavelength) sensitivity. Blue and green converge.', colors: this.tritanomalyColors },
          { name: 'Tritanopia', note: 'No functional S-cones. Blue and yellow hues are confused.', colors: this.tritanopiaColors },
        ],
      },
      {
        group: 'Monochromacy (no color)',
        items: [
          { name: 'Achromatomaly', note: 'Reduced function across all three cone types, yielding a heavily desaturated image.', colors: this.achromatomalyColors },
          { name: 'Achromatopsia', note: 'No functional cones. Perception reduces to luminance, seen as grayscale.', colors: this.achromatopsiaColors },
        ],
      },
    ];
  }

  /** Valid #RRGGBB colors from the current palette, in order. */
  private pickHeroColors(): string[] {
    const out: string[] = [];
    for ( const item of this.palette || [] ) {
      const raw = item?.value;
      if ( typeof raw !== 'string' ) {
        continue;
      }
      const hex = raw.startsWith( '#' ) ? raw : '#' + raw;
      if ( /^#[0-9a-fA-F]{6}$/.test( hex ) ) {
        out.push( hex );
      }
    }
    return out;
  }

  openRouteInNewTab( route: string ) {
    const url = this.router.serializeUrl( this.router.createUrlTree( [ route ] ) );
    window.open( url, '_blank' );
  }
}
